import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAuth0 } from "@auth0/auth0-react";
import { supabase } from '../supabaseClient';
import { Document, Page, Text, StyleSheet, pdf } from '@react-pdf/renderer';
import FormattedMessage from './FormattedQueryMessage';
import { FaQuestionCircle, FaArrowRight, FaSearch, FaMicrophone, FaStop } from 'react-icons/fa';
import { FiArrowUp, FiPlus } from 'react-icons/fi';
import { AnimatePresence, motion } from 'framer-motion';
import { useUsage, useTrialUpgradePrompt, notifyUsageUpdated, getBrowserTimezone } from '../hooks/useUsage';
import UpgradeNudge from './UpgradeNudge';
import UpgradeModal from './UpgradeModal';
import { GRADIENT_TEXT } from './caseStyles';
import Guide from './Guide';

const API_URL = process.env.NODE_ENV === 'production'
    ? 'https://api.petwise.vet'
    : 'http://localhost:3001';

const formatMessage = (content) => {
    // First, handle multiline LaTeX expressions before splitting into lines
    let processedContent = content;

    // Handle multiline display math expressions \[ ... \]
    processedContent = processedContent.replace(/\\\[([\s\S]*?)\\\]/g, (match, mathContent) => {
        // Clean up the math content
        let cleanMath = mathContent
            .replace(/\\text\{([^}]+)\}/g, '$1')  // Remove \text{...} commands
            .replace(/\\[,:;]/g, ' ')            // Convert spacing commands to spaces
            .replace(/\\quad/g, '&nbsp;&nbsp;&nbsp;&nbsp;')
            .replace(/\\qquad/g, '&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;')
            .trim();
        return `<div class="math-display">${cleanMath}</div>`;
    });

    // Handle inline LaTeX math expressions \( ... \) at content level
    processedContent = processedContent.replace(/\\\(([\s\S]*?)\\\)/g, (match, mathContent) => {
        // Clean up the math content
        let cleanMath = mathContent
            .replace(/\\text\{([^}]+)\}/g, '$1')  // Remove \text{...} commands
            .replace(/\\[,:;]/g, ' ')            // Convert spacing commands to spaces
            .replace(/\\quad/g, '&nbsp;&nbsp;&nbsp;&nbsp;')
            .replace(/\\qquad/g, '&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;')
            .trim();
        return `<span class="math-expression">${cleanMath}</span>`;
    });

    // Split into lines and find first non-empty line
    const lines = processedContent.split('\n');
    const firstNonEmptyLineIndex = lines.findIndex(line => line.trim().length > 0);

    // Handle title line separately (it's our main header)
    if (firstNonEmptyLineIndex !== -1) {
        const titleLine = lines[firstNonEmptyLineIndex].trim();
        lines[firstNonEmptyLineIndex] = `<h3>${titleLine}</h3>`;
    }

    // Remove any lines containing only "---"
    const filteredLines = lines.filter(line => line.trim() !== '---');

    // Remove consecutive empty lines (collapse multiple blank lines into one)
    const condensedLines = [];
    let lastLineEmpty = false;

    for (const line of filteredLines) {
        const isLineEmpty = line.trim() === '';

        // Only add empty line if previous line wasn't empty
        if (!(isLineEmpty && lastLineEmpty)) {
            condensedLines.push(line);
        }

        lastLineEmpty = isLineEmpty;
    }

    // Join lines with newlines, but use a string method to avoid extra whitespace
    let htmlContent = '';
    let lastElementType = null; // Track last element for smart spacing

    // Process line by line to have more control over the output
    for (let i = 0; i < condensedLines.length; i++) {
        const line = condensedLines[i].trim();

        // Skip empty lines but mark spacing opportunity
        if (line === '') {
            lastElementType = 'empty';
            continue;
        }

        // Process remaining mathematical expressions and LaTeX formatting
        let processedLine = line;

        // Handle inline math expressions with $ delimiters
        processedLine = processedLine.replace(/\$([^$]+)\$/g, '<span class="math-expression">$1</span>');

        // Handle text formatting issues like {text bold}
        processedLine = processedLine.replace(/\{([^}]+)\}/g, '$1');

        // Only process the bold text if it's not a header line (to avoid conflicts)
        if (
            !(/^(\d+)\.\s+(.*?):$/.test(line)) &&
            !(/^####\s+(.*)$/.test(line)) &&
            !(/^###\s+(.*)$/.test(line)) &&
            !(/^##\s+(.*)$/.test(line)) &&
            !(/^\*\*(.*?):\*\*$/.test(line))
        ) {
            // Process bold text
            processedLine = processedLine.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        }

        // Process different line types
        if (i === firstNonEmptyLineIndex) {
            // Title (already handled)
            htmlContent += `<h3>${processedLine.replace(/^<h3>(.*)<\/h3>$/, '$1')}</h3>`;
            lastElementType = 'title';
        }
        else if (/^(\d+)\.\s+(.*?):$/.test(processedLine)) {
            // Numbered section headers
            htmlContent += processedLine.replace(/^(\d+)\.\s+(.*?):$/, (match, num, text) => {
                // Remove any ** markers from section header text
                const cleanText = text.replace(/\*\*(.*?)\*\*/g, '$1');
                return `<div class="section-header"><span class="section-number">${num}.</span><h3>${cleanText}</h3></div>`;
            });
            lastElementType = 'section-header';
        }
        else if (/^####\s+(.*)$/.test(processedLine)) {
            // #### Headers
            htmlContent += processedLine.replace(/^####\s+(.*)$/, (match, text) => {
                // Remove any ** markers from header text
                const cleanText = text.replace(/\*\*(.*?)\*\*/g, '$1');
                return `<h3>${cleanText}</h3>`;
            });
            lastElementType = 'header';
        }
        else if (/^###\s+(.*)$/.test(processedLine)) {
            // ### Headers
            htmlContent += processedLine.replace(/^###\s+(.*)$/, (match, text) => {
                // Remove any ** markers from header text
                const cleanText = text.replace(/\*\*(.*?)\*\*/g, '$1');
                return `<h3>${cleanText}</h3>`;
            });
            lastElementType = 'header';
        }
        else if (/^##\s+(.*)$/.test(processedLine)) {
            // ## Headers - convert to div with bold-header class
            htmlContent += processedLine.replace(/^##\s+(.*)$/, (match, text) => {
                // Remove any ** markers from text
                const cleanText = text.replace(/\*\*(.*?)\*\*/g, '$1');
                return `<div class="bold-header">${cleanText}</div>`;
            });
            lastElementType = 'header';
        }
        else if (/^\*\*(.*?):\*\*$/.test(processedLine)) {
            // **Header:** format
            htmlContent += processedLine.replace(/^\*\*(.*?):\*\*$/, '<h3>$1</h3>');
            lastElementType = 'header';
        }
        else if (/^\s*-\s+(.*?):\s*$/.test(processedLine)) {
            // Bullet points with nested info (medication headers) - no bullets
            htmlContent += processedLine.replace(/^\s*-\s+(.*?):\s*$/, '<div class="medication-item"><strong>$1:</strong></div>');
            lastElementType = 'medication';
        }
        else if (/^\s*-\s+(.*)$/.test(processedLine)) {
            // Regular bullet points - convert to plain text without bullets
            const contentWithoutBullet = processedLine.replace(/^\s*-\s+/, '');
            htmlContent += `<div class="line-item">${contentWithoutBullet}</div>`;
            lastElementType = 'line-item';
        }
        else if (/^(\d+)\.\s+(.*)$/.test(processedLine)) {
            // Numbered list items
            htmlContent += processedLine.replace(/^(\d+)\.\s+(.*)$/, '<div class="list-item"><span class="number">$1.</span><span>$2</span></div>');
            lastElementType = 'list-item';
        }
        else {
            // Special handling for additional notes and recommendations
            if (processedLine.includes("Additional Notes:") || processedLine.startsWith("**Additional Notes")) {
                htmlContent += `<div class="additional-notes">${processedLine}</div>`;
                lastElementType = 'notes';
            }
            else if (processedLine.includes("Recommendation:") || processedLine.includes("Recommendation**") || processedLine.startsWith("**Recommendation")) {
                htmlContent += `<div class="recommendation">${processedLine}</div>`;
                lastElementType = 'recommendation';
            }
            // Regular text - preserve as is
            else {
                htmlContent += `<div class="text-line">${processedLine}</div>`;
                lastElementType = 'text';
            }
        }
    }

    return htmlContent;
};

const formatMessageForPDF = (content) => {
    const styles = StyleSheet.create({
        page: {
            padding: 40,
            fontSize: 12,
            fontFamily: 'Helvetica',
            lineHeight: 1.4
        },
        text: {
            marginBottom: 4,
            fontFamily: 'Helvetica'
        },
        header: {
            fontFamily: 'Helvetica-Bold',
            fontSize: 13,
            marginTop: 16,
            marginBottom: 8
        },
        bold: {
            fontFamily: 'Helvetica-Bold'
        },
        listItem: {
            marginLeft: 20,
            marginBottom: 4
        }
    });

    const formatParagraphs = (text) => {
        const lines = text.split('\n');
        return lines.map((line, index) => {
            // Check if it's a header (ends with ':' or matches number pattern)
            if (line.endsWith(':') && !line.match(/^\d+\./)) {
                return (
                    <Text key={index} style={styles.header}>
                        {line.replace(/\*\*/g, '')}
                    </Text>
                );
            }

            // Handle numbered sections with bold parts
            if (line.match(/^\d+\.\s+\*\*/)) {
                const parts = line.split(/(\*\*[^*]+\*\*)/g);
                return (
                    <Text key={index} style={styles.text}>
                        {parts.map((part, partIndex) => {
                            if (part.startsWith('**') && part.endsWith('**')) {
                                return (
                                    <Text key={partIndex} style={styles.bold}>
                                        {part.replace(/\*\*/g, '')}
                                    </Text>
                                );
                            }
                            return part;
                        })}
                    </Text>
                );
            }

            // Handle bold text within lines
            if (line.includes('**')) {
                const parts = line.split(/(\*\*[^*]+\*\*)/g);
                return (
                    <Text key={index} style={styles.text}>
                        {parts.map((part, partIndex) => {
                            if (part.startsWith('**') && part.endsWith('**')) {
                                return (
                                    <Text key={partIndex} style={styles.bold}>
                                        {part.replace(/\*\*/g, '')}
                                    </Text>
                                );
                            }
                            return part;
                        })}
                    </Text>
                );
            }

            // Regular text
            return <Text key={index} style={styles.text}>{line}</Text>;
        });
    };

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                {formatParagraphs(content)}
            </Page>
        </Document>
    );
};

const SUGGESTIONS = [
    {
        category: "Treatment Protocols",
        question: "What is the step-by-step treatment protocol for severe canine parvovirus, including fluid rates and antibiotic choices?"
    },
    {
        category: "Emergency Medicine",
        question: "What are the exact dosages and timing for diazepam, midazolam, and phenobarbital in status epilepticus?"
    },
    {
        category: "Medication Guidance",
        question: "For resistant feline UTIs, what culture-based antibiotic protocols do you recommend after amoxicillin failure?"
    },
    {
        category: "Dermatology",
        question: "What's the current ICADA protocol for severe canine atopic dermatitis, including Apoquel and Cytopoint timing?"
    },
    {
        category: "Surgery",
        question: "What pre-op lab work and medication adjustments are needed for dental procedures in cats over 12 years?"
    },
    {
        category: "Oncology",
        question: "What's the CHOP protocol dosing schedule for intermediate to high-grade feline lymphoma?"
    },
    {
        category: "Cardiology",
        question: "What's the emergency furosemide protocol for acute CHF with respiratory distress, including CRI rates?"
    },
    {
        category: "Neurology",
        question: "What's the diagnostic protocol for differentiating central vs peripheral vestibular disease in geriatric dogs?"
    },
    {
        category: "Endocrinology",
        question: "What's the insulin CRI protocol and fluid rates for treating DKA in cats with BG >400?"
    },
    {
        category: "Emergency Medicine",
        question: "What's the complete protocol for severe acute pancreatitis in dogs, including fluid rates and pain management?"
    },
    {
        category: "Ophthalmology",
        question: "What's the treatment protocol for deep stromal corneal ulcers in cats, including surgical options?"
    },
    {
        category: "Toxicology",
        question: "What's the decontamination and treatment protocol for dogs that ingested >3oz dark chocolate?"
    },
    {
        category: "Reproduction",
        question: "What's the medical and surgical protocol for managing dystocia in French Bulldogs?"
    },
    {
        category: "Gastroenterology",
        question: "What's the current stepwise approach for treating refractory IBD in cats after diet trials fail?"
    },
    {
        category: "Orthopedics",
        question: "What's the detailed 8-week post-op rehab protocol following TPLO surgery in large breed dogs?"
    },
    {
        category: "Behavior",
        question: "What are the current dosing protocols for fluoxetine and trazodone for severe separation anxiety?"
    },
    {
        category: "Critical Care",
        question: "What's the protocol for treating Eastern Diamondback rattlesnake bites, including antivenom dosing?"
    },
    {
        category: "Respiratory",
        question: "What's the emergency and maintenance protocol for severe feline asthma, including inhaler options?"
    },
    {
        category: "Nutrition",
        question: "What's the detailed refeeding protocol for hepatic lipidosis in cats, including tube feeding schedules?"
    },
    {
        category: "Dentistry",
        question: "What antibiotics and doses are recommended for stage 4 periodontal disease with osteomyelitis?"
    }
];

const QuickQuery = ({ isMobile = false }) => {
    const { user } = useAuth0();
    const [userData, setUserData] = useState(null);
    const [messages, setMessages] = useState(() => {
        const saved = localStorage.getItem('quickQueryMessages');
        return saved ? JSON.parse(saved) : [];
    });
    const [inputMessage, setInputMessage] = useState(() => {
        return localStorage.getItem('quickQueryInput') || '';
    });
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const textareaRef = useRef(null);
    const [copiedIndex, setCopiedIndex] = useState(null);
    const [fadeOutLoader, setFadeOutLoader] = useState(false);
    const [randomSuggestions] = useState(
        SUGGESTIONS.sort(() => Math.random() - 0.5).slice(0, 3)
    );
    const [isTyping, setIsTyping] = useState(false);
    const [showSources, setShowSources] = useState({});
    const [isLongAnswerMode, setIsLongAnswerMode] = useState(false);
    const [showTutorial, setShowTutorial] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const mediaRecorderRef = useRef(null);
    const streamRef = useRef(null);
    const audioChunksRef = useRef([]);

    // Free-tier daily usage: show nothing below 80%, banner at 3 queries left,
    // upgrade screen at 0. Dismissal is keyed to the local date so it resets daily.
    const usage = useUsage();
    const trialPrompt = useTrialUpgradePrompt(usage);
    const [lastUsage, setLastUsage] = useState(null);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const nudgeKey = `petquery-nudge-dismissed-${new Date().toDateString()}`;
    const [nudgeDismissed, setNudgeDismissed] = useState(
        () => sessionStorage.getItem(nudgeKey) === 'true'
    );
    const queryRemaining = lastUsage
        ? Math.max(lastUsage.limit - lastUsage.used, 0)
        : usage.query.remaining;
    const showNudge = usage.loaded && !usage.isUnlimited && !nudgeDismissed && queryRemaining > 0 && queryRemaining <= 3;
    const dismissNudge = () => {
        setNudgeDismissed(true);
        sessionStorage.setItem(nudgeKey, 'true');
    };

    // Check for tutorial flag from Help center
    useEffect(() => {
        const openTutorialFlag = localStorage.getItem('openPetQueryTutorial');
        if (openTutorialFlag === 'true') {
            setShowTutorial(true);
            localStorage.removeItem('openPetQueryTutorial');
        }
    }, []);

    useEffect(() => {
        const lastUser = localStorage.getItem('lastUserId');
        const currentUser = user?.sub;

        if (lastUser && currentUser && lastUser !== currentUser) {
            localStorage.removeItem('quickQueryMessages');
            setMessages([]);
        }

        if (currentUser) {
            localStorage.setItem('lastUserId', currentUser);
        }
    }, [user]);

    useEffect(() => {
        if (messages.length > 0) {
            // One scheduled scroll avoids competing smooth-scroll animations on iOS.
            scrollToBottom(80);
        }
    }, [messages]);

    // Scroll when loader appears
    useEffect(() => {
        if (isLoading) {
            scrollToBottom(60);
        }
    }, [isLoading]);

    useEffect(() => {
        localStorage.setItem('quickQueryMessages', JSON.stringify(messages));
    }, [messages]);

    useEffect(() => {
        localStorage.setItem('quickQueryInput', inputMessage);
        // Auto-resize textarea when input changes (including from transcription)
        if (textareaRef.current) {
            if (isMobile) {
                textareaRef.current.style.height = '22px';
                textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 96)}px`;
            } else if (!inputMessage.trim()) {
                textareaRef.current.style.height = '52px';
            } else {
                textareaRef.current.style.height = '52px';
                textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
            }
        }
    }, [inputMessage, isMobile]);

    useEffect(() => {
        // Opening the keyboard automatically makes the mobile/PWA viewport jump.
        if (textareaRef.current && !isLoading && !isMobile) {
            textareaRef.current.focus();
        }
    }, [isLoading, isMobile]);

    useEffect(() => {
        const fetchUserData = async () => {
            if (!user?.sub) return;

            try {
                const { data, error } = await supabase
                    .from('users')
                    .select('dvm_name')
                    .eq('auth0_user_id', user.sub)
                    .single();

                if (!error && data) {
                    setUserData(data);
                }
            } catch (err) {
                console.error('Error fetching user data:', err);
            }
        };

        fetchUserData();
    }, [user]);

    const scrollToBottom = (delay = 50) => {
        // Use direct scroll on the container instead of scrollIntoView
        // This ensures we scroll to the actual bottom, accounting for the fixed footer
        // Small delay ensures DOM has updated before scrolling
        setTimeout(() => {
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTo({
                    top: scrollContainerRef.current.scrollHeight,
                    behavior: "smooth"
                });
            }
        }, delay);
    };

    useEffect(() => {
        if (!isMobile || !window.visualViewport) return undefined;

        let animationFrame;
        const handleViewportResize = () => {
            cancelAnimationFrame(animationFrame);
            animationFrame = requestAnimationFrame(() => {
                if (scrollContainerRef.current) {
                    scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
                }
            });
        };

        window.visualViewport.addEventListener('resize', handleViewportResize);
        return () => {
            cancelAnimationFrame(animationFrame);
            window.visualViewport.removeEventListener('resize', handleViewportResize);
        };
    }, [isMobile]);

    const formatTimestamp = () => {
        const now = new Date();
        const hours = now.getHours().toString().padStart(2, '0');
        const minutes = now.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
    };

    // Helper function to manage loading state transitions
    const finishLoading = () => {
        // Immediately hide the loader
        setIsLoading(false);
        setIsTyping(false);
        setFadeOutLoader(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!inputMessage.trim() || isLoading) return;

        // Preemptive free-tier check — saves a wasted API round-trip at 100%
        if (!usage.isUnlimited && usage.query.remaining <= 0) {
            setShowUpgradeModal(true);
            return;
        }

        // Get timestamp
        const timestamp = formatTimestamp();

        const newMessage = {
            role: 'user',
            content: inputMessage.trim(),
            timestamp
        };
        setMessages(prev => [...prev, newMessage]);
        setInputMessage('');
        setIsLoading(true);
        setIsTyping(true);
        setFadeOutLoader(false);

        // Collapse textarea
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = '56px';
        }

        try {
            // Usage is counted server-side (free-tier monthly cap)
            const systemPrompt = isLongAnswerMode
                ? `You are PetQuery, a **Veterinary Assistant AI** providing comprehensive, in-depth responses for **licensed veterinarians only**. IMPORTANT: Always assume you are speaking with a licensed veterinarian. Give detailed, thorough answers that cover all relevant aspects of the question. Provide extensive clinical information, background context, differential diagnoses, treatment options, prognosis, and detailed explanations. Include comprehensive sources and references. ${userData?.dvm_name ? `You are speaking with Dr. ${userData.dvm_name}. Address them as such.` : ''}`
                : ` You are PetQuery, a **Veterinary Assistant AI** providing concise, professional responses for **licensed veterinarians only**. IMPORTANT: Always assume you are speaking with a licensed veterinarian. Your responses should be **short, precise, and rich in clinical information** while adhering to the following formatting:

### FORMATTING GUIDELINES:
1. **Title:** The FIRST line of every response must be a short plain-text title for the answer (no numbering, no markdown headers).
2. **Headers:** Use **bold headers** for clear sectioning.
3. **Spacing:** Ensure proper spacing with **double line breaks** between sections and headers.
4. **Lists:** Number top-level sections sequentially (1., 2., 3. ...) and NEVER restart numbering within a response. For items nested under a section, use "-" bullets or bold sub-headers instead of numbers.
5. **Critical Terms:** Highlight key terms or actions in **bold** for clarity.
6. **Drug details:** Always present drug details as one "**Label:** value" line each (e.g. **Drug Name:**, **Dose:**, **Route:**, **Frequency:**, **Duration:**, **Additional Notes:**).

### RESPONSE GUIDELINES:
- Responses should focus on actionable, evidence-based information, staying concise and on-topic.
- For common cases, prioritize practical treatments.
- For less common or specific cases, provide insightful, clinically relevant advice with brief reasoning.
- **Avoid lengthy explanations** unless explicitly requested.

### WHEN PROVIDING TREATMENTS:
Treatments should follow this structure:
1. **Medication Category**
   **Drug Name:** Include generic name and examples of brand names (if applicable).
   **Dose:** X mg/kg
   **Route:** (IV, PO, SQ, IM)
   **Frequency:** (e.g., SID, BID, TID, QID)
   **Duration:** X days
 **Additional Notes:** Include contraindications, warnings, or monitoring guidelines if relevant.

**Example:**
1. **Fluid Therapy**
    **Drug Name:** Isotonic crystalloid solution (e.g., LRS or 0.9% NaCl)
        **Dose:** 10-20 mL/kg IV bolus, then maintenance based on ongoing losses
        **Frequency:** As needed to maintain hydration
        **Duration:** Adjust based on hydration status
        **Additional Notes:** Monitor for fluid overload, particularly in cardiac or renal patients.

### WHEN PROVIDING DIAGNOSTIC PLANS:
Diagnostics should be presented as actionable steps, prioritized based on the case. Use this structure:

1. **Primary Objective:** State the purpose of the diagnostic (e.g., rule out X condition, confirm Y finding).

2. **Recommended Diagnostics:**
   - Test/Procedure 1: Brief explanation of its purpose or clinical relevance.
   - Test/Procedure 2: Continue with the next diagnostic steps, as needed.

**Example:**
1. **Diagnostics for Anemia in Canines**
   **Primary Objective:** Determine the cause and severity of anemia.
   **Recommended Diagnostics:**
     CBC with reticulocyte count: Assess severity and regenerative response.
     Coombs' test: Evaluate for immune-mediated hemolysis.
     Abdominal ultrasound: Rule out splenic masses or bleeding.

### WHEN HANDLING UNCOMMON CASES:
For less common cases, provide:
1. A brief overview of the condition.
2. Recommended treatment or diagnostic steps.
3. Any relevant clinical notes or key considerations.

**Example:**
**Case: Canine Tetany**
1. **Overview:** Tetany often results from hypocalcemia secondary to eclampsia, parathyroid dysfunction, or chronic renal failure.
2. **Treatment:**
   **Drug Name:** Calcium gluconate
   **Dose:** 0.5-1.5 mL/kg IV slowly over 10-30 minutes
   **Route:** IV
   **Frequency:** Single dose, repeat based on ionized calcium levels
   **Additional Notes:** Monitor ECG for bradycardia or arrhythmias during infusion.

### SOURCES REQUIREMENT:
Always include a comprehensive **Sources** section at the end of your response with detailed, properly formatted citations including:
- Full author names and publication years
- Complete journal names with volume/issue numbers when available
- Publisher information for textbooks
- Specific page ranges or DOI numbers when applicable
- Professional organization guideline titles and availability
- Manufacturer prescribing information with proper attribution
- Include 4-6 high-quality, relevant sources minimum
- Format as numbered list with proper academic citation style
- Include brief explanatory text about the sources' relevance when helpful

Example format:
**Sources:**
1. Ettinger, S. J., & Feldman, E. C. (2017). *Textbook of Veterinary Internal Medicine*. Elsevier.
2. Greene, S.A., et al. (2008). "Management of snake envenomation in dogs," *Journal of Veterinary Emergency and Critical Care*, 18(5), 526-532.
3. American College of Veterinary Emergency and Critical Care (ACVECC) guidelines on emergency toxicology management.

### FINAL TOUCHES:
- Always end responses with a clear **Recommendation** section summarizing the next steps.
- Always allow any language translations.
- Always allow client handouts or client communications.
- ${userData?.dvm_name ? `You are speaking with Dr. ${userData.dvm_name}. Address them as such.` : ''}
- Provide **specific and concise information** for maximum clarity and usability.

### Example Output:
**Case: Acute Canine Pancreatitis**

1. **Primary Objective:** Stabilize the patient and reduce pancreatic inflammation.
2. **Treatment:**
   **Fluid Therapy:**
     **Drug Name:** Isotonic crystalloid (e.g., LRS or 0.9% NaCl)
     **Dose:** 10-20 mL/kg IV bolus, then maintenance
     **Frequency:** Continuous infusion
     **Duration:** Until hydration status is restored
   **Pain Management:**
     **Drug Name:** Buprenorphine
     **Dose:** 0.01-0.02 mg/kg
     **Route:** IM or IV
     **Frequency:** QID
     **Duration:** 3-5 days
3. **Recommendation:** Administer fluids immediately and control pain. Monitor electrolytes and hydration. Reassess within 24 hours with follow-up diagnostics (e.g., CPL).

**Sources:**
1. Veterinary Textbooks:
   - "Textbook of Veterinary Internal Medicine" by Ettinger and Feldman
2. Peer-Reviewed Journals:
   - "Management of acute pancreatitis in dogs," Journal of Veterinary Emergency and Critical Care
3. Professional Organizations:
   - ACVECC consensus guidelines on acute pancreatitis management

By adhering to these guidelines, ensure responses are **short, actionable, and formatted for quick reference** while providing high-quality assistance tailored to professional veterinarians.
`;

            const conversationHistory = [
                {
                    role: 'system',
                    content: systemPrompt
                },
                ...messages.slice(-5),
                newMessage
            ];

            const response = await axios.post(
                `${API_URL}/api/quickquery`,
                {
                    model: 'gpt-5.4-mini',
                    messages: conversationHistory,
                    max_tokens: isLongAnswerMode ? 4000 : 2000,
                    temperature: 0.7,
                    top_p: 0.9,
                    frequency_penalty: 0.5,
                    presence_penalty: 0.5,
                    user: { sub: user?.sub },
                    source: 'petquery',
                    tz: getBrowserTimezone()
                }
            );

            if (response.data.error) {
                throw new Error(response.data.error);
            }

            setLastUsage(response.data.usage_petwise || null);
            notifyUsageUpdated();
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: response.data.choices[0].message.content,
                timestamp: formatTimestamp()
            }]);
        } catch (error) {
            console.error('Error in QuickQuery:', error);
            let errorMessage = "I'm sorry, I encountered an error. Please try again.";

            if (error.response?.status === 403 && error.response?.data?.error === 'USAGE_LIMIT_REACHED') {
                setShowUpgradeModal(true);
                errorMessage = "You've finished today's free PetQuery questions. Your free allowance resets at midnight, or upgrade anytime for unlimited use.";
            } else if (error.response?.status === 429) {
                // Check if it's a quota error from OpenAI
                const detail = error.response?.data?.detail || '';
                if (detail.includes('insufficient_quota') || detail.includes('quota')) {
                    errorMessage = "OpenAI API quota exceeded. Please check your OpenAI account billing or wait for your quota to reset.";
                } else {
                    errorMessage = "Too many requests. Please try again in a moment.";
                }
            } else if (error.response?.data?.error === 'Daily report limit reached') {
                errorMessage = "You've reached your daily query limit. Please upgrade your plan or try again tomorrow.";
            }

            setMessages(prev => [...prev, {
                role: 'assistant',
                content: errorMessage,
                timestamp: formatTimestamp()
            }]);
        } finally {
            finishLoading();
        }
    };

    const handleClear = () => {
        setMessages([]);
        localStorage.removeItem('quickQueryMessages');
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            // Ensure all audio tracks are enabled and active
            stream.getAudioTracks().forEach(track => {
                track.enabled = true;
            });

            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = async () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                await transcribeAudio(audioBlob);
                stream.getTracks().forEach(track => track.stop());
            };

            // Set recording state immediately so UI updates right away
            setIsRecording(true);

            // Start recording immediately with a small timeslice (50ms) for continuous capture
            // The timeslice ensures data is captured in frequent chunks, minimizing loss at the start
            mediaRecorder.start(50);
        } catch (err) {
            console.error('Error starting recording:', err);
            alert('Failed to access microphone. Please check permissions.');
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
        }
    };

    const transcribeAudio = async (audioBlob) => {
        setIsTranscribing(true);
        try {
            const formData = new FormData();
            formData.append('audio', audioBlob, 'recording.webm');

            const response = await axios.post(`${API_URL}/api/transcribe-simple`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.text) {
                // Append transcribed text to existing input
                const transcribedText = response.data.text.trim();
                setInputMessage(prev => {
                    const existing = prev.trim();
                    if (existing) {
                        return `${existing} ${transcribedText}`;
                    }
                    return transcribedText;
                });
            } else {
                throw new Error('No transcription received');
            }
        } catch (err) {
            console.error('Transcription error:', err);
            alert('Failed to transcribe audio. Please try again.');
        } finally {
            setIsTranscribing(false);
        }
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }
        };
    }, []);

    const handleCopy = async (text, index) => {
        // Format the text by removing markdown
        const formattedText = text
            .replace(/###\s*(.*?)(?:\n|$)/g, '$1')  // Remove ### markers
            .replace(/\*\*(.*?)\*\*/g, '$1')  // Remove ** markers
            .split('\n')
            .map(line => line.trim())
            .join('\n');

        try {
            // Create HTML content with proper formatting
            const formattedHtml = formatMessage(text);

            const clipboardItem = new ClipboardItem({
                'text/html': new Blob([`<div style="font-family: Arial, sans-serif; line-height: 1.5; color: black; white-space: pre-wrap; padding: 0; margin: 0;">${formattedHtml}</div>`], { type: 'text/html' }),
                'text/plain': new Blob([formattedText], { type: 'text/plain' })
            });

            await navigator.clipboard.write([clipboardItem]);
            setCopiedIndex(index);
            setTimeout(() => setCopiedIndex(null), 2000);
        } catch (err) {
            try {
                await navigator.clipboard.writeText(formattedText);
            } catch {
                await navigator.clipboard.writeText(text);
            }
            setCopiedIndex(index);
            setTimeout(() => setCopiedIndex(null), 2000);
        }
    };

    const handlePrint = async (content) => {
        try {
            const doc = formatMessageForPDF(content);
            const blob = await pdf(doc).toBlob();
            const url = URL.createObjectURL(blob);

            const printWindow = window.open(url, '_blank');
            printWindow.onload = () => {
                printWindow.print();
                printWindow.onafterprint = () => {
                    printWindow.close();
                    URL.revokeObjectURL(url);
                };
            };
        } catch (error) {
            console.error('Print error:', error);
        }
    };

    const handleSuggestionClick = async (question) => {
        if (isTyping) return;
        if (!usage.isUnlimited && usage.query.remaining <= 0) {
            setShowUpgradeModal(true);
            return;
        }

        // Get timestamp
        const timestamp = formatTimestamp();

        const newMessage = {
            role: 'user',
            content: question,
            timestamp
        };
        setMessages(prev => [...prev, newMessage]);
        setIsTyping(true);
        setIsLoading(true);
        setFadeOutLoader(false);

        // Auto-scroll to the bottom
        scrollToBottom();

        try {
            const systemPrompt = isLongAnswerMode
                ? `You are a **Veterinary Assistant AI** providing comprehensive, in-depth responses for **licensed veterinarians only**. IMPORTANT: Always assume you are speaking with a licensed veterinarian. Give detailed, thorough answers that cover all relevant aspects of the question. Provide extensive clinical information, background context, differential diagnoses, treatment options, prognosis, and detailed explanations. Include comprehensive sources and references. ${userData?.dvm_name ? `You are speaking with Dr. ${userData.dvm_name}. Address them as such.` : ''}`
                : ` You are a **Veterinary Assistant AI** providing concise, professional responses for **licensed veterinarians only**. IMPORTANT: Always assume you are speaking with a licensed veterinarian. Your responses should be **short, precise, and rich in clinical information** while adhering to the following formatting:

### FORMATTING GUIDELINES:
1. **Title:** The FIRST line of every response must be a short plain-text title for the answer (no numbering, no markdown headers).
2. **Headers:** Use **bold headers** for clear sectioning.
3. **Spacing:** Ensure proper spacing with **double line breaks** between sections and headers.
4. **Lists:** Number top-level sections sequentially (1., 2., 3. ...) and NEVER restart numbering within a response. For items nested under a section, use "-" bullets or bold sub-headers instead of numbers.
5. **Critical Terms:** Highlight key terms or actions in **bold** for clarity.
6. **Drug details:** Always present drug details as one "**Label:** value" line each (e.g. **Drug Name:**, **Dose:**, **Route:**, **Frequency:**, **Duration:**, **Additional Notes:**).

### RESPONSE GUIDELINES:
- Responses should focus on actionable, evidence-based information, staying concise and on-topic.
- For common cases, prioritize practical treatments.
- For less common or specific cases, provide insightful, clinically relevant advice with brief reasoning.
- **Avoid lengthy explanations** unless explicitly requested.
- When giving dosages use Plumbs as the primary source first.

### WHEN PROVIDING TREATMENTS:
Treatments should follow this structure:
1. **Medication Category**
   **Drug Name:** Include generic name and examples of brand names (if applicable).
   **Dose:** X mg/kg
   **Route:** (IV, PO, SQ, IM)
   **Frequency:** (e.g., SID, BID, TID, QID)
   **Duration:** X days
 **Additional Notes:** Include contraindications, warnings, or monitoring guidelines if relevant.

**Example:**
1. **Fluid Therapy**
    **Drug Name:** Isotonic crystalloid solution (e.g., LRS or 0.9% NaCl)
        **Dose:** 10-20 mL/kg IV bolus, then maintenance based on ongoing losses
        **Frequency:** As needed to maintain hydration
        **Duration:** Adjust based on hydration status
        **Additional Notes:** Monitor for fluid overload, particularly in cardiac or renal patients.

### WHEN PROVIDING DIAGNOSTIC PLANS:
Diagnostics should be presented as actionable steps, prioritized based on the case. Use this structure:

1. **Primary Objective:** State the purpose of the diagnostic (e.g., rule out X condition, confirm Y finding).

2. **Recommended Diagnostics:**
   - Test/Procedure 1: Brief explanation of its purpose or clinical relevance.
   - Test/Procedure 2: Continue with the next diagnostic steps, as needed.

**Example:**
1. **Diagnostics for Anemia in Canines**
   **Primary Objective:** Determine the cause and severity of anemia.
   **Recommended Diagnostics:**
     CBC with reticulocyte count: Assess severity and regenerative response.
     Coombs' test: Evaluate for immune-mediated hemolysis.
     Abdominal ultrasound: Rule out splenic masses or bleeding.

### WHEN HANDLING UNCOMMON CASES:
For less common cases, provide:
1. A brief overview of the condition.
2. Recommended treatment or diagnostic steps.
3. Any relevant clinical notes or key considerations.

**Example:**
**Case: Canine Tetany**
1. **Overview:** Tetany often results from hypocalcemia secondary to eclampsia, parathyroid dysfunction, or chronic renal failure.
2. **Treatment:**
   **Drug Name:** Calcium gluconate
   **Dose:** 0.5-1.5 mL/kg IV slowly over 10-30 minutes
   **Route:** IV
   **Frequency:** Single dose, repeat based on ionized calcium levels
   **Additional Notes:** Monitor ECG for bradycardia or arrhythmias during infusion.

### SOURCES REQUIREMENT:
Always include a comprehensive **Sources** section at the end of your response with detailed, properly formatted citations including:
- Full author names and publication years
- Complete journal names with volume/issue numbers when available
- Publisher information for textbooks
- Specific page ranges or DOI numbers when applicable
- Professional organization guideline titles and availability
- Manufacturer prescribing information with proper attribution
- Include 4-6 high-quality, relevant sources minimum
- Format as numbered list with proper academic citation style
- Include brief explanatory text about the sources' relevance when helpful

Example format:
**Sources:**
1. Ettinger, S. J., & Feldman, E. C. (2017). *Textbook of Veterinary Internal Medicine*. Elsevier.
2. Greene, S.A., et al. (2008). "Management of snake envenomation in dogs," *Journal of Veterinary Emergency and Critical Care*, 18(5), 526-532.
3. American College of Veterinary Emergency and Critical Care (ACVECC) guidelines on emergency toxicology management.

### FINAL TOUCHES:
- Always end responses with a clear **Recommendation** section summarizing the next steps.
- Always allow any language translations.
- Always allow client handouts or client communications.
- ${userData?.dvm_name ? `You are speaking with Dr. ${userData.dvm_name}. Address them as such.` : ''}
- Provide **specific and concise information** for maximum clarity and usability.

### Example Output:
**Case: Acute Canine Pancreatitis**

1. **Primary Objective:** Stabilize the patient and reduce pancreatic inflammation.
2. **Treatment:**
   **Fluid Therapy:**
     **Drug Name:** Isotonic crystalloid (e.g., LRS or 0.9% NaCl)
     **Dose:** 10-20 mL/kg IV bolus, then maintenance
     **Frequency:** Continuous infusion
     **Duration:** Until hydration status is restored
   **Pain Management:**
     **Drug Name:** Buprenorphine
     **Dose:** 0.01-0.02 mg/kg
     **Route:** IM or IV
     **Frequency:** QID
     **Duration:** 3-5 days
3. **Recommendation:** Administer fluids immediately and control pain. Monitor electrolytes and hydration. Reassess within 24 hours with follow-up diagnostics (e.g., CPL).

**Sources:**
1. Veterinary Textbooks:
   - "Textbook of Veterinary Internal Medicine" by Ettinger and Feldman
2. Peer-Reviewed Journals:
   - "Management of acute pancreatitis in dogs," Journal of Veterinary Emergency and Critical Care
3. Professional Organizations:
   - ACVECC consensus guidelines on acute pancreatitis management

By adhering to these guidelines, ensure responses are **short, actionable, and formatted for quick reference** while providing high-quality assistance tailored to professional veterinarians.
`;

            const conversationHistory = [
                {
                    role: 'system',
                    content: systemPrompt
                },
                ...messages.slice(-5),
                {
                    role: 'user',
                    content: question
                }
            ];

            const response = await axios.post(
                `${API_URL}/api/quickquery`,
                {
                    model: 'gpt-5.4-mini',
                    messages: conversationHistory,
                    max_tokens: isLongAnswerMode ? 4000 : 2000,
                    temperature: 0.7,
                    top_p: 0.9,
                    frequency_penalty: 0.5,
                    presence_penalty: 0.5,
                    user: { sub: user?.sub },
                    source: 'petquery',
                    tz: getBrowserTimezone()
                }
            );

            if (response.data.error) {
                throw new Error(response.data.error);
            }

            setLastUsage(response.data.usage_petwise || null);
            notifyUsageUpdated();
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: response.data.choices[0].message.content,
                timestamp: formatTimestamp()
            }]);
        } catch (error) {
            console.error('Error in QuickQuery:', error);
            let errorMessage = "I'm sorry, I encountered an error. Please try again.";

            if (error.response?.status === 403 && error.response?.data?.error === 'USAGE_LIMIT_REACHED') {
                setShowUpgradeModal(true);
                errorMessage = "You've finished today's free PetQuery questions. Your free allowance resets at midnight, or upgrade anytime for unlimited use.";
            } else if (error.response?.status === 429) {
                // Check if it's a quota error from OpenAI
                const detail = error.response?.data?.detail || '';
                if (detail.includes('insufficient_quota') || detail.includes('quota')) {
                    errorMessage = "OpenAI API quota exceeded. Please check your OpenAI account billing or wait for your quota to reset.";
                } else {
                    errorMessage = "Too many requests. Please try again in a moment.";
                }
            }

            setMessages(prev => [...prev, {
                role: 'assistant',
                content: errorMessage,
                timestamp: formatTimestamp()
            }]);
        } finally {
            finishLoading();
        }
    };

    // Helper function to detect if user is explicitly asking for sources
    const isSourcesQuery = (userMessage) => {
        if (!userMessage) return false;
        const lowerMessage = userMessage.toLowerCase();
        const sourceKeywords = [
            'sources', 'source', 'references', 'citations', 'cite',
            'where did you get', 'what are your sources', 'give me the sources',
            'show me sources', 'provide sources', 'list sources',
            'reference', 'bibliography', 'documentation'
        ];
        return sourceKeywords.some(keyword => lowerMessage.includes(keyword));
    };

    // Helper function to extract sources from message content
    const extractSources = (content, userMessage = '') => {
        // If user explicitly asked for sources, don't extract them - show as main content
        if (isSourcesQuery(userMessage)) {
            return null;
        }

        // Try multiple patterns to match sources section
        const patterns = [
            /\*\*Sources:\*\*([\s\S]*?)(?=\n\n\*\*|$)/i,
            /\*\*Sources\*\*([\s\S]*?)(?=\n\n\*\*|$)/i,
            /Sources:([\s\S]*?)(?=\n\n\*\*|$)/i,
            /\*\*References:\*\*([\s\S]*?)(?=\n\n\*\*|$)/i,
        ];

        for (const pattern of patterns) {
            const match = content.match(pattern);
            if (match) {
                return match[1].trim();
            }
        }

        return null;
    };

    // Helper function to get content without sources
    const getContentWithoutSources = (content, userMessage = '') => {
        // If user explicitly asked for sources, don't remove them
        if (isSourcesQuery(userMessage)) {
            return content;
        }

        const patterns = [
            /\*\*Sources:\*\*([\s\S]*?)(?=\n\n\*\*|$)/i,
            /\*\*Sources\*\*([\s\S]*?)(?=\n\n\*\*|$)/i,
            /Sources:([\s\S]*?)(?=\n\n\*\*|$)/i,
            /\*\*References:\*\*([\s\S]*?)(?=\n\n\*\*|$)/i,
        ];

        let cleanContent = content;
        for (const pattern of patterns) {
            cleanContent = cleanContent.replace(pattern, '').trim();
        }

        return cleanContent;
    };

    // Helper function to toggle sources display
    const toggleSources = (index) => {
        setShowSources(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const sendButton = (
        <button
            type="submit"
            className={`${isMobile ? 'w-9 h-9 active:scale-95 touch-manipulation' : 'w-8 h-8 cursor-pointer hover:bg-[#2350a3]'} rounded-full border-none bg-[#2d5fb8] text-white flex items-center justify-center flex-shrink-0 transition-colors duration-200 disabled:bg-[#e7ecf3] disabled:text-[#1a2b4a]/35 disabled:cursor-not-allowed`}
            disabled={isLoading || !inputMessage.trim() || isRecording}
            aria-label="Send message"
            title="Send"
        >
            <FiArrowUp className={isMobile ? 'text-[18px]' : 'text-[16px]'} strokeWidth={2.5} />
        </button>
    );

    return (
        <>
            <style dangerouslySetInnerHTML={{
                __html: `
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                    }
                    to {
                        opacity: 1;
                    }
                }
                @keyframes slideUpScale {
                    from {
                        opacity: 0;
                        transform: translateY(20px) scale(0.95);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }
                .hide-scrollbar {
                    scrollbar-width: none; /* Firefox */
                    -ms-overflow-style: none; /* IE and Edge */
                }
                .hide-scrollbar::-webkit-scrollbar {
                    display: none; /* Chrome, Safari, Opera */
                }
                .mobile-scroll-container {
                    -webkit-overflow-scrolling: touch;
                    overscroll-behavior-y: contain;
                }
                textarea {
                    -webkit-appearance: none;
                    -moz-appearance: none;
                    appearance: none;
                }
                textarea::placeholder {
                    opacity: 1;
                    color: #9ca3af;
                }
            `}} />
            <AnimatePresence>
                {(showUpgradeModal || trialPrompt.show) && (
                    <UpgradeModal
                        user={{ ...user, ...userData }}
                        feature="query"
                        reason={trialPrompt.show ? trialPrompt.reason : 'limit'}
                        resetsAt={lastUsage?.resetsAt || usage.resetsAt}
                        onClose={() => {
                            setShowUpgradeModal(false);
                            trialPrompt.dismiss();
                        }}
                        onSubscribed={() => usage.refresh()}
                    />
                )}
            </AnimatePresence>
            <div className={`${isMobile ? 'h-full overflow-hidden bg-[#f5f7fb]' : 'h-screen overflow-hidden bg-gradient-to-b from-[#f6f8fb] to-white'} flex flex-col`} style={{ fontFamily: "var(--font-sans)" }}>
                {!isMobile && messages.length > 0 && (
                    <div className="flex-shrink-0 border-b border-[#eef2f7] bg-white/80 backdrop-blur-md">
                        <div className="max-w-6xl mx-auto h-[60px] px-4 flex items-center justify-between">
                            <h2 className={`m-0 pb-0.5 text-[22px] font-bold tracking-[-0.02em] ${GRADIENT_TEXT}`}>PetQuery</h2>
                            <button
                                type="button"
                                onClick={handleClear}
                                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[13px] font-semibold text-[#13233f] bg-white border border-[#e7ecf3] hover:bg-[#f6f8fb] cursor-pointer transition-colors"
                            >
                                New chat
                            </button>
                        </div>
                    </div>
                )}
                <div className="flex-1 flex flex-col overflow-hidden">
                    <div ref={scrollContainerRef} className={`flex-1 min-h-0 overflow-y-auto ${isMobile ? 'px-3.5 py-4 mobile-scroll-container' : 'px-4 py-8'} space-y-4`}>
                        <UpgradeNudge show={showNudge} feature="query" remaining={queryRemaining} onDismiss={dismissNudge} />
                        {/* Mobile empty state */}
                        {messages.length === 0 && isMobile && (
                            <motion.div
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, ease: 'easeOut' }}
                                className="min-h-full flex flex-col justify-center py-4"
                            >
                                <div className="text-center px-5 mb-6">
                                    <div className="flex items-center justify-center gap-2.5 flex-wrap mb-2">
                                        <h1 className={`m-0 pb-1 text-[2.25rem] leading-[1.15] font-bold tracking-[-0.03em] ${GRADIENT_TEXT}`}>
                                            PetQuery
                                        </h1>
                                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-amber-50 text-amber-700 border border-amber-200">
                                            Mobile Beta
                                        </span>
                                    </div>
                                    <p className="text-sm leading-relaxed text-gray-500">
                                        Ask about differentials, treatment protocols, dosages, or diagnostics.
                                    </p>
                                </div>
                                <div className="space-y-2">
                                    {randomSuggestions.map((suggestion, index) => (
                                        <motion.button
                                            key={suggestion.question}
                                            type="button"
                                            initial={{ opacity: 0, y: 8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.25, delay: 0.08 + index * 0.06 }}
                                            whileTap={{ scale: 0.985 }}
                                            onClick={() => handleSuggestionClick(suggestion.question)}
                                            className="w-full flex items-center gap-3 text-left rounded-2xl border border-gray-200/90 bg-white px-4 py-3.5 shadow-[0_2px_10px_rgba(15,23,42,0.04)]"
                                        >
                                            <div className="w-9 h-9 flex-shrink-0 rounded-xl bg-blue-50 text-[#3468bd] flex items-center justify-center">
                                                <FaSearch className="text-sm" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="text-[11px] font-bold uppercase tracking-wide text-[#3468bd] mb-0.5">
                                                    {suggestion.category}
                                                </p>
                                                <p className="text-[13px] leading-snug text-gray-700 line-clamp-2">
                                                    {suggestion.question}
                                                </p>
                                            </div>
                                            <FaArrowRight className="flex-shrink-0 text-xs text-gray-300" />
                                        </motion.button>
                                    ))}
                                </div>
                                <p className="text-center text-[10px] leading-relaxed text-gray-400 px-6 mt-5">
                                    AI-generated clinical support. Verify findings before treatment decisions.
                                </p>
                            </motion.div>
                        )}
                        {/* Desktop empty state with suggestions */}
                        {messages.length === 0 && !isMobile && (
                            <div className="max-w-5xl mx-auto pt-[6vh]">
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, ease: [0.2, 0.7, 0.2, 1] }}
                                    className="text-center mb-10"
                                >
                                    <div className="flex items-center justify-center gap-2">
                                        <h1 className={`m-0 pb-1 text-[2.5rem] leading-[1.15] font-bold tracking-[-0.03em] ${GRADIENT_TEXT}`}>PetQuery</h1>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowTutorial(true);
                                            }}
                                            title="How PetQuery works"
                                            className="w-7 h-7 rounded-full flex items-center justify-center text-[#1a2b4a]/35 hover:text-[#2d5fb8] hover:bg-white bg-transparent border-none cursor-pointer transition-colors"
                                        >
                                            <FaQuestionCircle className="text-[15px]" />
                                        </button>
                                    </div>
                                    <p className="mt-1.5 mb-0 text-[15px] text-[#1a2b4a]/55">
                                        Ask about differentials, dosages, treatment protocols or diagnostics.
                                    </p>
                                </motion.div>
                                <p className="mb-3 ml-1 text-[11px] font-semibold tracking-[0.12em] uppercase text-[#1a2b4a]/45">Try asking</p>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {randomSuggestions.map((suggestion, index) => (
                                        <motion.div
                                            key={suggestion.question}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.35, ease: [0.2, 0.7, 0.2, 1], delay: 0.05 + index * 0.04 }}
                                        >
                                            <motion.button
                                                type="button"
                                                whileHover={{ y: -4 }}
                                                whileTap={{ scale: 0.985 }}
                                                transition={{ type: 'spring', stiffness: 420, damping: 30 }}
                                                onClick={() => handleSuggestionClick(suggestion.question)}
                                                className="group w-full h-full text-left rounded-[24px] border border-[#e7ecf3] bg-white p-5 cursor-pointer transition-[box-shadow,border-color] duration-300 hover:border-[#d6e3f7] hover:shadow-[0_24px_48px_-24px_rgba(19,35,63,0.25)] flex flex-col"
                                            >
                                                <span className="text-[11.5px] font-semibold tracking-[0.06em] uppercase text-[#2d5fb8]">{suggestion.category}</span>
                                                <span className="mt-2 text-[14.5px] leading-relaxed text-[#13233f]/85 flex-1">{suggestion.question}</span>
                                                <span className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-[#2d5fb8] opacity-0 group-hover:opacity-100 transition-opacity">
                                                    Ask this <FaArrowRight className="text-[10px]" />
                                                </span>
                                            </motion.button>
                                        </motion.div>
                                    ))}
                                </div>
                                <p className="mt-8 mb-0 text-center text-[12px] leading-relaxed text-[#1a2b4a]/40 max-w-2xl mx-auto">
                                    PetQuery gives AI-generated answers for educational use. They can contain mistakes and are not a substitute for professional judgment, so verify before making treatment decisions.
                                </p>
                            </div>
                        )}
                        {messages.map((msg, index) => {
                            // Find the user message that prompted this assistant response
                            const getUserMessage = (assistantIndex) => {
                                // Look backwards from the assistant message to find the most recent user message
                                for (let i = assistantIndex - 1; i >= 0; i--) {
                                    if (messages[i].role === 'user') {
                                        return messages[i].content;
                                    }
                                }
                                return '';
                            };

                            const userMessage = msg.role === 'assistant' ? getUserMessage(index) : '';

                            return (
                                <motion.div
                                    key={index}
                                    initial={isMobile ? { opacity: 0, y: 8, scale: 0.99 } : false}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    transition={{ duration: 0.25, ease: 'easeOut' }}
                                    className={`flex ${isMobile ? 'mb-3 max-w-none' : 'mb-6 max-w-6xl mx-auto w-full'} ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                >
                                    <div className={`${isMobile ? (msg.role === 'user' ? 'max-w-[86%]' : 'max-w-[94%]') : msg.role === 'user' ? 'max-w-[70%]' : 'w-full'} ${msg.role === 'user'
                                        ? `bg-[#2d5fb8] text-white ${isMobile ? 'rounded-[20px] rounded-br-md px-4 py-2.5 text-sm shadow-sm' : 'rounded-[22px] rounded-br-md px-5 py-3.5 text-[15px] shadow-[0_10px_30px_-16px_rgba(45,95,184,0.7)]'} ml-auto`
                                        : `${isMobile ? 'bg-white text-gray-800 rounded-[20px] rounded-bl-md px-4 py-3 text-sm border border-gray-200/80 shadow-[0_2px_12px_rgba(15,23,42,0.05)] message-content' : 'bg-[#e8f0fc] text-[#13233f] rounded-[28px] rounded-bl-md px-10 py-8 border border-[#2d5fb8]/[0.14]'}`
                                        }`}>
                                        {msg.role === 'assistant' ? (
                                            <FormattedMessage content={getContentWithoutSources(msg.content, userMessage)} />
                                        ) : (
                                            <div className="whitespace-pre-wrap">{msg.content}</div>
                                        )}
                                        {msg.role === 'assistant' && (
                                            <div className={`flex items-center gap-1.5 ${isMobile ? 'mt-2' : 'mt-6'}`}>
                                                <button
                                                    className={`inline-flex items-center justify-center ${isMobile ? 'p-1.5 rounded active:scale-95 touch-manipulation' : 'w-8 h-8 rounded-full cursor-pointer'} text-xs border transition-colors duration-200 ${copiedIndex === index
                                                        ? 'bg-[#2d5fb8] text-white border-[#2d5fb8]'
                                                        : 'bg-transparent text-[#1a2b4a]/50 border-[#2d5fb8]/15 hover:text-[#2d5fb8] hover:bg-white active:bg-gray-100'
                                                        }`}
                                                    onClick={() => handleCopy(getContentWithoutSources(msg.content, userMessage), index)}
                                                    aria-label="Copy message"
                                                >
                                                    {copiedIndex === index ? (
                                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width={isMobile ? "14" : "16"} height={isMobile ? "14" : "16"}>
                                                            <path fillRule="evenodd" d="M19.916 4.626a.75.75 0 01.208 1.04l-9 13.5a.75.75 0 01-1.154.114l-6-6a.75.75 0 011.06-1.06l5.353 5.353 8.493-12.739a.75.75 0 011.04-.208z" clipRule="evenodd" />
                                                        </svg>
                                                    ) : (
                                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width={isMobile ? "14" : "16"} height={isMobile ? "14" : "16"}>
                                                            <path d="M7.5 3.375c0-1.036.84-1.875 1.875-1.875h.375a3.75 3.75 0 013.75 3.75v1.875C13.5 8.161 14.34 9 15.375 9h1.875A3.75 3.75 0 0121 12.75v3.375C21 17.16 20.16 18 19.125 18h-9.75A1.875 1.875 0 017.5 16.125V3.375z" />
                                                            <path d="M15 5.25a5.23 5.23 0 00-1.279-3.434 9.768 9.768 0 016.963 6.963A5.23 5.23 0 0017.25 7.5h-1.875A.375.375 0 0115 7.125V5.25zM4.875 6H6v10.125A3.375 3.375 0 009.375 19.5H16.5v1.125c0 1.035-.84 1.875-1.875 1.875h-9.75A1.875 1.875 0 013 20.625V7.875C3 6.839 3.84 6 4.875 6z" />
                                                        </svg>
                                                    )}
                                                </button>
                                                {!isMobile && (
                                                    <button
                                                        className="inline-flex items-center justify-center w-8 h-8 rounded-full cursor-pointer bg-transparent text-[#1a2b4a]/50 border border-[#2d5fb8]/15 hover:text-[#2d5fb8] hover:bg-[#f6f8fb] transition-colors duration-200"
                                                        onClick={() => handlePrint(getContentWithoutSources(msg.content, userMessage))}
                                                        aria-label="Print message"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                                                            <path fillRule="evenodd" d="M7.875 1.5C6.839 1.5 6 2.34 6 3.375v2.99c-.426.053-.851.11-1.274.174-1.454.218-2.476 1.483-2.476 2.917v6.294a3 3 0 003 3h.27l-.155 1.705A1.875 1.875 0 007.232 22.5h9.536a1.875 1.875 0 001.867-2.045l-.155-1.705h.27a3 3 0 003-3V9.456c0-1.434-1.022-2.7-2.476-2.917A48.716 48.716 0 0018 6.366V3.375c0-1.036-.84-1.875-1.875-1.875h-8.25zM16.5 6.205v-2.83A.375.375 0 0016.125 3h-8.25a.375.375 0 00-.375.375v2.83a49.353 49.353 0 019 0zm-.217 8.265c.178.018.317.16.333.337l.526 5.784a.375.375 0 01-.374.409H7.232a.375.375 0 01-.374-.409l.526-5.784a.373.373 0 01.333-.337 41.741 41.741 0 018.566 0zm.967-3.97a.75.75 0 01.75-.75h.008a.75.75 0 01.75.75v.008a.75.75 0 01-.75.75H18a.75.75 0 01-.75-.75V10.5zM15 9.75a.75.75 0 00-.75.75v.008c0 .414.336.75.75.75h.008a.75.75 0 00.75-.75V10.5a.75.75 0 00-.75-.75H15z" clipRule="evenodd" />
                                                        </svg>
                                                    </button>
                                                )}
                                                {extractSources(msg.content, userMessage) && (
                                                    <button
                                                        className={`inline-flex items-center justify-center ${isMobile ? 'p-1.5 rounded' : 'w-8 h-8 rounded-full cursor-pointer'} text-xs border transition-colors duration-200 ${showSources[index]
                                                            ? 'bg-[#e9f1fc] text-[#2d5fb8] border-[#2d5fb8]/30'
                                                            : 'bg-transparent text-[#1a2b4a]/50 border-[#2d5fb8]/15 hover:text-[#2d5fb8] hover:bg-white active:bg-gray-100'
                                                            }`}
                                                        onClick={() => toggleSources(index)}
                                                        aria-label="Show sources"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width={isMobile ? "14" : "16"} height={isMobile ? "14" : "16"}>
                                                            <path d="M11.25 4.533A9.707 9.707 0 006 3a9.735 9.735 0 00-3.25.555.75.75 0 00-.5.707v14.25a.75.75 0 001 .707A8.237 8.237 0 016 18.75c1.995 0 3.823.707 5.25 1.886V4.533zM12.75 20.636A8.214 8.214 0 0118 18.75c.966 0 1.89.166 2.75.47a.75.75 0 001-.708V4.262a.75.75 0 00-.5-.707A9.735 9.735 0 0018 3a9.707 9.707 0 00-5.25 1.533v16.103z" />
                                                        </svg>
                                                    </button>
                                                )}
                                                {!isMobile && <span className="text-[12px] text-[#1a2b4a]/40 ml-auto">{formatTimestamp()}</span>}
                                            </div>
                                        )}
                                        {msg.role === 'assistant' && showSources[index] && extractSources(msg.content, userMessage) && (
                                            <div className="mt-5 pt-5 border-t border-[#2d5fb8]/10 text-sm text-[#1a2b4a]/70 animate-fade-in-up">
                                                <FormattedMessage content={extractSources(msg.content, userMessage)} />
                                            </div>
                                        )}
                                        {msg.role === 'user' && !isMobile && (
                                            <div className="text-xs text-white/70 mt-2 text-right">{formatTimestamp()}</div>
                                        )}
                                    </div>
                                </motion.div>
                            );
                        })}
                        {isLoading && (
                            <motion.div
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                className={`flex justify-start mb-6 ${isMobile ? 'max-w-none' : 'max-w-6xl mx-auto w-full'} ${fadeOutLoader ? 'animate-fade-out' : ''}`}
                            >
                                <div className={isMobile
                                    ? 'h-10 px-4 rounded-[20px] rounded-bl-md bg-white border border-gray-200/80 shadow-sm flex items-center gap-1.5'
                                    : 'shimmer-loader'
                                }>
                                    {isMobile ? (
                                        <>
                                            {[0, 1, 2].map((dot) => (
                                                <motion.span
                                                    key={dot}
                                                    className="w-1.5 h-1.5 rounded-full bg-[#3468bd]"
                                                    animate={{ y: [0, -4, 0], opacity: [0.4, 1, 0.4] }}
                                                    transition={{ duration: 0.8, repeat: Infinity, delay: dot * 0.14 }}
                                                />
                                            ))}
                                        </>
                                    ) : (
                                        <span className="loader-text">Thinking...</span>
                                    )}
                                </div>
                            </motion.div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                    <div className={`relative flex-shrink-0 ${isMobile ? 'bg-white border-t border-gray-200/80 shadow-[0_-8px_24px_rgba(15,23,42,0.05)]' : 'bg-white/85 backdrop-blur-md border-t border-[#eef2f7]'}`}>
                        <div className={`mx-auto ${isMobile ? 'px-3.5 pt-2.5 pb-3' : 'px-4 pt-3 pb-5 max-w-3xl'}`}>
                            {/* Mode toggle - smaller on mobile */}
                            <div className={`flex justify-center items-center gap-2 ${isMobile ? 'mb-2.5' : 'mb-3'}`}>
                                <div className={`inline-flex rounded-full ${isMobile ? 'bg-gray-100 p-0.5 border border-gray-200/70' : 'bg-[#f3f6fa] p-1 gap-1'}`}>
                                    {[['Short', false], ['Long', true]].map(([label, long]) => {
                                        const on = isLongAnswerMode === long;
                                        return (
                                            <button
                                                key={label}
                                                type="button"
                                                onClick={() => setIsLongAnswerMode(long)}
                                                className={`relative ${isMobile ? 'px-3 py-1 text-[11px] active:scale-95 touch-manipulation' : 'px-4 h-8 text-[13px]'} font-semibold rounded-full border-none bg-transparent cursor-pointer transition-colors duration-200 ${on ? 'text-[#2d5fb8]' : 'text-[#1a2b4a]/50 hover:text-[#13233f]'}`}
                                            >
                                                {on && (
                                                    <motion.span
                                                        layoutId="query-length"
                                                        className="absolute inset-0 rounded-full bg-white shadow-sm"
                                                        transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                                                    />
                                                )}
                                                <span className="relative">{isMobile ? label : `${label} answer`}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                                {/* Mobile clear button - compact */}
                                {messages.length > 0 && isMobile && (
                                    <button
                                        onClick={handleClear}
                                        type="button"
                                        className="px-2.5 py-1 text-xs bg-gray-100 text-gray-500 rounded-full font-medium active:scale-95 transition-transform touch-manipulation"
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                            {/* Input form - ChatGPT style on mobile */}
                            <form onSubmit={handleSubmit} className={`flex ${isMobile ? 'gap-2 items-end' : 'relative gap-3 items-end'}`}>
                                {isMobile ? (
                                    <div className={`relative flex-1 min-h-[46px] max-h-[112px] rounded-[23px] border transition-all duration-200 flex items-end ${
                                        isRecording
                                            ? 'bg-red-50 border-red-200'
                                            : 'bg-gray-100 border-gray-200 focus-within:bg-white focus-within:border-blue-300 focus-within:ring-4 focus-within:ring-blue-50'
                                    }`}>
                                        <textarea
                                            ref={textareaRef}
                                            value={inputMessage}
                                            onChange={(e) => {
                                                setInputMessage(e.target.value);
                                                e.target.style.height = '22px';
                                                e.target.style.height = `${Math.min(e.target.scrollHeight, 96)}px`;
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    if (inputMessage.trim() && !isLoading) {
                                                        handleSubmit(e);
                                                        e.target.style.height = '22px';
                                                    }
                                                }
                                            }}
                                            placeholder={isRecording ? 'Listening… tap stop when finished' : 'Ask a clinical question…'}
                                            className="w-full min-h-[22px] max-h-[96px] my-3 ml-4 mr-[84px] bg-transparent border-0 outline-none resize-none placeholder-gray-400 overflow-y-auto hide-scrollbar"
                                            style={{
                                                height: '22px',
                                                maxHeight: '96px',
                                                padding: '0',
                                                fontSize: '16px',
                                                lineHeight: '22px',
                                                fontFamily: 'inherit',
                                                WebkitAppearance: 'none',
                                                MozAppearance: 'none'
                                            }}
                                            disabled={isLoading || isRecording}
                                            rows="1"
                                        />
                                        <div className="absolute right-2 bottom-1.5 flex items-center">
                                            {!isRecording && !isTranscribing && (
                                                <button
                                                    type="button"
                                                    onClick={startRecording}
                                                    className="w-9 h-9 rounded-full text-gray-500 active:text-[#3468bd] active:bg-blue-50 active:scale-95 transition-all touch-manipulation flex items-center justify-center"
                                                    aria-label="Start dictation"
                                                >
                                                    <FaMicrophone className="text-[15px]" />
                                                </button>
                                            )}
                                            {isRecording && (
                                                <button
                                                    type="button"
                                                    onClick={stopRecording}
                                                    className="w-9 h-9 rounded-full bg-red-500 text-white shadow-sm animate-pulse active:scale-95 transition-transform touch-manipulation flex items-center justify-center"
                                                    aria-label="Stop recording"
                                                >
                                                    <FaStop className="text-sm" />
                                                </button>
                                            )}
                                            {!isRecording && sendButton}
                                            {isTranscribing && (
                                                <div className="w-9 h-9 text-[#3468bd] flex items-center justify-center">
                                                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    /* Desktop input */
                                    <div className="relative flex-1">
                                        <textarea
                                            ref={textareaRef}
                                            value={inputMessage}
                                            onChange={(e) => {
                                                setInputMessage(e.target.value);
                                                if (!e.target.value.trim()) {
                                                    e.target.style.height = '52px';
                                                } else {
                                                    e.target.style.height = '52px';
                                                    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
                                                }
                                            }}
                                            onClick={(e) => {
                                                if (inputMessage.trim()) {
                                                    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
                                                }
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    if (inputMessage.trim() && !isLoading) {
                                                        e.target.style.height = '52px';
                                                        handleSubmit(e);
                                                    }
                                                }
                                            }}
                                            placeholder="Ask me anything about veterinary medicine..."
                                            className="block w-full min-h-[52px] max-h-[120px] pl-5 pr-24 py-[14px] text-[15px] text-[#13233f] placeholder:text-[#1a2b4a]/40 border border-[#e7ecf3] rounded-[26px] bg-white focus:border-[#2d5fb8]/35 focus:outline-none shadow-[0_10px_30px_-20px_rgba(19,35,63,0.35)] resize-none transition-colors duration-200 disabled:opacity-70 disabled:cursor-not-allowed hide-scrollbar overflow-y-auto"
                                            disabled={isLoading || isRecording}
                                            rows="1"
                                        />
                                        {/* Desktop mic and collapse buttons */}
                                        <div className="absolute bottom-[10px] right-2.5 flex items-center gap-1">
                                            {!isRecording && !isTranscribing && (
                                                <button
                                                    type="button"
                                                    onClick={startRecording}
                                                    className="w-8 h-8 flex items-center justify-center text-[#2d5fb8] bg-transparent border-none cursor-pointer transition-colors duration-200 rounded-full hover:bg-[#f6f8fb]"
                                                    aria-label="Start dictation"
                                                    title="Start dictation"
                                                >
                                                    <FaMicrophone className="text-[15px]" />
                                                </button>
                                            )}
                                            {isRecording && (
                                                <button
                                                    type="button"
                                                    onClick={stopRecording}
                                                    className="p-1.5 text-red-500 hover:text-red-600 transition-colors duration-200 rounded-full hover:bg-red-50 animate-pulse"
                                                    aria-label="Stop recording"
                                                    title="Stop recording"
                                                >
                                                    <FaStop className="text-lg" />
                                                </button>
                                            )}
                                            {isTranscribing && (
                                                <div className="p-1.5 text-primary-500">
                                                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                </div>
                                            )}
                                            {sendButton}
                                        </div>
                                    </div>
                                )}
                                {!isMobile && messages.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={handleClear}
                                        className="absolute left-full bottom-[8px] ml-3 inline-flex items-center gap-1 h-9 px-3.5 whitespace-nowrap rounded-full text-[13px] font-semibold text-[#13233f] bg-white border border-[#e7ecf3] hover:border-[#2d5fb8]/30 hover:text-[#2d5fb8] cursor-pointer transition-colors flex-shrink-0 shadow-[0_10px_30px_-20px_rgba(19,35,63,0.35)]"
                                    >
                                        <FiPlus className="text-[13px]" />
                                        New chat
                                    </button>
                                )}
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            <AnimatePresence>
                {showTutorial && <Guide id="petquery" onClose={() => setShowTutorial(false)} />}
            </AnimatePresence>
        </>
    );
};

export default QuickQuery; 